import React from 'react';
import { Link } from 'react-router-dom';

// Matches **bold**, *italic*, `code`, and [text](url) in that order.
const INLINE = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
const LINK = /^\[([^\]]+)\]\(([^)]+)\)$/;

// Strips the same inline syntax back to plain text. Needed where a real element
// can't be nested — the index wraps each row in a Link, so a title containing a
// markdown link would put an <a> inside an <a>.
export const plain = (text = '') =>
  text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1');

// Renders a subset of markdown inline syntax so posts can be written as plain strings.
export default function RichText({ text }) {
  if (!text) return null;

  const parts = text.split(INLINE).filter(Boolean);

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
          return <em key={index}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return <code key={index}>{part.slice(1, -1)}</code>;
        }
        const link = part.match(LINK);
        if (link) {
          const [, label, href] = link;

          // Same-page anchor, e.g. [sweep](#sweep) — never leaves the post.
          if (href.startsWith('#')) {
            return (
              <a key={index} href={href}>
                {label}
              </a>
            );
          }

          // A site route gets a router Link so it doesn't reload the app. A path
          // with a file extension is a static asset in /public, not a route —
          // routing to it would hit the SPA fallback and render the app instead
          // of the file, so it stays a plain anchor.
          const isRoute = href.startsWith('/') && !/\.[a-z0-9]+$/i.test(href);
          if (isRoute) {
            return (
              <Link key={index} to={href}>
                {label}
              </Link>
            );
          }

          return (
            <a key={index} href={href} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
}
